import React, { useState, useEffect, useRef } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Home, BarChart2, DollarSign, MoreHorizontal, Youtube, Disc, CheckSquare, ArrowLeft } from "react-feather";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from 'react-router-dom';
import { usePartnerProgram } from "../contexts/PartnerProgramContext";
import { partnerMenuFor } from "../lib/partnerNav";
import { useUser } from '../contexts/UserContext';
import { usesPartnerLayout } from '../lib/partnerNav';

const FILLABLE_ICONS = new Set([Home, Youtube, Disc, MoreHorizontal]);

const REVENUE_PATHS = ['/revenue', '/total'];

const isPathActive = (pathname, path) =>
  Boolean(path) && (pathname === path || pathname.startsWith(`${path}/`));
// 「내 완성 쇼츠」(/ready-shorts)는 검수 화면 옆 탭이라 「검수」 탭이 켜진다(2026-10-07)
const NAV_ALSO_ACTIVE = { "/video-inspection": ["/ready-shorts"] };

const SLIDE_TRANSITION = 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1)';

function BottomNavBar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useUser();
  const partnerCtx = usePartnerProgram(); // PartnerLayout 안에서만 값이 있다 — 크리에이터 화면에서는 null
  const [showRevenueSub, setShowRevenueSub] = useState(() => REVENUE_PATHS.includes(window.location.pathname));
  const userClosedRef = useRef(false);

  const isDisabled = userInfo?.is_not_contract || userInfo?.grade === "CUT";
  // 파트너 탭 — M1 하단 탭형(2026-09-09): 홈 · 가이드 · 유치 · 정산 (문의·레이 없음).
  // 판정은 UserLayout 의 파트너 레이아웃과 같은 함수(usesPartnerLayout, 역할 OR 경로) — 레이아웃과 탭이 어긋나지 않게.
  // Partner 단독은 허용된 계정 화면(/edit-profile 등)에서도 파트너 탭(QA 2026-09-09), Admin 겸직은 /partner* 에서만(2026-09-11)
  const isPartnerSettlementPage = usesPartnerLayout(userInfo?.role, location.pathname);

  useEffect(() => {
    if (REVENUE_PATHS.includes(location.pathname)) {
      if (!userClosedRef.current) {
        setShowRevenueSub(true);
      }
    } else {
      userClosedRef.current = false;
    }
  }, [location.pathname]);

  // 라벨은 번역 사전에서 온다(`src/i18n/locales/*.json` nav.*). 하단 탭은 칸 폭이 고정이라
  // 번역 문구를 영문 8자 이내로 유지한다 — 넘으면 줄임표가 생겨 메뉴를 못 읽는다(docs/ops/app-i18n-plan.md §6-3).
  const mainNavItems = [
    { label: t("nav.home"), path: "/home", icon: Home, disabled: isDisabled },
    { label: t("nav.channel"), path: "/channel", icon: Youtube, disabled: isDisabled },
    { label: t("nav.album"), path: "/album", icon: Disc, disabled: isDisabled },
    { label: t("nav.inspection"), path: "/video-inspection", icon: CheckSquare, disabled: isDisabled },
    { label: t("nav.revenue"), path: null, icon: DollarSign, disabled: isDisabled, action: "toggleRevenueSub" },
  ];

  const subNavItems = [
    { label: t("nav.menu"), path: null, icon: ArrowLeft, disabled: false, action: "backToMain" },
    { label: t("nav.revenueExpect"), path: "/revenue", icon: BarChart2, disabled: isDisabled },
    { label: t("nav.total"), path: "/total", icon: DollarSign, disabled: false },
  ];

  // 파트너 탭 — 유형별(partnerMenuFor, 최대 4개). 라벨 키 nav.partner* 는 ko/en/ja/zh.json (2026-09-11)
  const partnerNavItems = partnerMenuFor(partnerCtx?.program || null, { mobile: true })
    .map((m) => ({ label: t(m.navKey, { defaultValue: m.labelKo }), path: m.path, icon: m.icon, disabled: false, exact: Boolean(m.exact) }));

  if (isPartnerSettlementPage) {
    return (
      <NavWrapper>
        <NavFlex>
          {partnerNavItems.map((item, idx) => (
            <NavItem key={idx} item={item} isActive={item.exact ? location.pathname === item.path : isPathActive(location.pathname, item.path)} onClick={() => navigate(item.path)} />
          ))}
        </NavFlex>
      </NavWrapper>
    );
  }

  const handleItemClick = (item) => {
    if (item.disabled) return;
    if (item.action === "toggleRevenueSub") {
      setShowRevenueSub(true);
      navigate("/revenue");
      return;
    }
    if (item.action === "backToMain") {
      userClosedRef.current = true;
      setShowRevenueSub(false);
      return;
    }
    if (item.path) {
      navigate(item.path);
    }
  };

  const getIsActive = (item) => {
    if (item.action === "toggleRevenueSub") {
      return REVENUE_PATHS.includes(location.pathname);
    }
    if (item.action === "backToMain") return false;
    return isPathActive(location.pathname, item.path) || (NAV_ALSO_ACTIVE[item.path] || []).some((p) => isPathActive(location.pathname, p));
  };

  return (
    <Box
      position="fixed"
      left={0}
      right={0}
      bottom={0}
      zIndex={100}
      w="100%"
      display={{ base: 'block', md: 'none' }}
    >
      {/* 메인 하단바 */}
      <Box
        bg="white"
        pt={"4px"}
        pb={"max(4px, env(safe-area-inset-bottom))"}
        borderTop="0.5px solid"
        borderColor="neutral.200"
        opacity={showRevenueSub ? 0 : 1}
        transition="opacity 0.3s ease"
        pointerEvents={showRevenueSub ? "none" : "auto"}
        w="100%"
      >
        <NavFlex>
          {mainNavItems.map((item, idx) => (
            <NavItem
              key={item.path || item.action || idx}
              item={item}
              isActive={getIsActive(item)}
              onClick={() => handleItemClick(item)}
            />
          ))}
        </NavFlex>
      </Box>

      {/* 보조 하단바 */}
      <Box
        position="absolute"
        left="12px"
        right="12px"
        bottom="calc(6px + env(safe-area-inset-bottom))"
        bg="white"
        borderRadius="16px"
        border="1px solid"
        borderColor="neutral.200"
        boxShadow="0 -2px 16px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)"
        transform={showRevenueSub ? "translateX(0)" : "translateX(calc(100% + 24px))"}
        opacity={showRevenueSub ? 1 : 0}
        transition={SLIDE_TRANSITION}
        pointerEvents={showRevenueSub ? "auto" : "none"}
      >
        <NavFlex>
          {subNavItems.map((item, idx) => {
            const isBack = item.action === "backToMain";
            return (
              <Flex
                key={item.path || item.action || idx}
                flex={1}
                direction="column"
                align="center"
                justify="center"
                gap="2px"
                py="4px"
                opacity={item.disabled ? 0.3 : 1}
                cursor={item.disabled ? 'not-allowed' : 'pointer'}
                onClick={item.disabled ? undefined : () => handleItemClick(item)}
                transition="all 0.2s ease"
                _active={!item.disabled ? { transform: 'scale(0.85)' } : {}}
                sx={{ WebkitTapHighlightColor: 'transparent' }}
                role="group"
              >
                {isBack ? (
                  <Box
                    color="neutral.400"
                    transition="color 0.2s ease"
                  >
                    <ArrowLeft size={22} stroke="currentColor" strokeWidth={1.4} />
                  </Box>
                ) : (
                  <Box
                    color={getIsActive(item) ? 'brand.600' : 'neutral.400'}
                    transition="color 0.2s ease"
                  >
                    <item.icon
                      size={22}
                      stroke={getIsActive(item) && FILLABLE_ICONS.has(item.icon) ? 'white' : 'currentColor'}
                      strokeWidth={getIsActive(item) ? (FILLABLE_ICONS.has(item.icon) ? 1.6 : 2.2) : 1.4}
                      fill={getIsActive(item) && FILLABLE_ICONS.has(item.icon) ? 'currentColor' : 'none'}
                    />
                  </Box>
                )}
                <Text
                  fontSize="12px"
                  lineHeight="1"
                  fontWeight={getIsActive(item) ? '600' : '400'}
                  color={getIsActive(item) ? 'brand.600' : 'neutral.400'}
                  transition="all 0.2s ease"
                >
                  {item.label}
                </Text>
              </Flex>
            );
          })}
        </NavFlex>
      </Box>
    </Box>
  );
}

/* 공통 Flex 래퍼 */
function NavFlex({ children }) {
  return (
    <Flex w="100%" py="6px" px="2px" justify="space-around" align="center">
      {children}
    </Flex>
  );
}

/* 공통 Nav 아이템 (메인 하단바용) */
function NavItem({ item, isActive, onClick }) {
  const IconComponent = item.icon;
  return (
    <Flex
      flex={1}
      direction="column"
      align="center"
      justify="center"
      gap="2px"
      py="4px"
      opacity={item.disabled ? 0.3 : 1}
      cursor={item.disabled ? 'not-allowed' : 'pointer'}
      onClick={item.disabled ? undefined : onClick}
      transition="all 0.2s ease"
      _active={!item.disabled ? { transform: 'scale(0.9)' } : {}}
      sx={{ WebkitTapHighlightColor: 'transparent' }}
      role="group"
    >
      <Box
        color={isActive ? 'brand.600' : 'neutral.400'}
        transition="color 0.2s ease"
      >
        <IconComponent
          size={22}
          stroke={isActive && FILLABLE_ICONS.has(IconComponent) ? 'white' : 'currentColor'}
          strokeWidth={isActive ? (FILLABLE_ICONS.has(IconComponent) ? 1.6 : 2.2) : 1.4}
          fill={isActive && FILLABLE_ICONS.has(IconComponent) ? 'currentColor' : 'none'}
        />
      </Box>
      <Text
        fontSize="12px"
        lineHeight="1"
        fontWeight={isActive ? '600' : '400'}
        color={isActive ? 'brand.600' : 'neutral.400'}
        transition="all 0.2s ease"
      >
        {item.label}
      </Text>
    </Flex>
  );
}

/* 공통 고정 래퍼 */
function NavWrapper({ children }) {
  return (
    <Box
      position="fixed"
      left={0}
      right={0}
      bottom={0}
      zIndex={100}
      w="100%"
      display={{ base: 'block', md: 'none' }}
      pb="env(safe-area-inset-bottom)"
      bg="white"
      borderTop="0.5px solid"
      borderColor="neutral.200"
    >
      {children}
    </Box>
  );
}

export default React.memo(BottomNavBar);
